<template>
    <v-dialog
        v-model="show"
        persistent
        eager
        :width="displayWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card
            class="scrollable-container pb-2"
            height="450px"
            role="region"
            aria-label="card-upload-devices"
        >
            <v-card-title role="region" aria-label="card-header">
                <v-container pa-0 fluid>
                    <v-row no-gutters>
                        <v-col>{{ displayTitle }}</v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="py-2">
                            <v-file-input
                                v-model="selectedFile"
                                dense
                                show-size
                                hide-details
                                outlined
                                accept=".xlsx,.csv"
                                prepend-icon=""
                                :disabled="isDataLoading"
                                :prepend-inner-icon="icons.file"
                                placeholder="Click here to select a file"
                                aria-label="file-name"
                            >
                                <template v-slot:append-outer>
                                    <v-tooltip
                                        v-model="showHelp"
                                        left
                                        role="region"
                                        aria-label="file-upload-tooltip"
                                    >
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on"></span>
                                        </template>
                                        <div
                                            class="pa-2"
                                            style="max-width: 550px;"
                                        >
                                            <div>
                                                This dialog allows for bulk
                                                upload of Devices using a csv,
                                                xlsx, or xls file. The file has
                                                five columns, with the headers
                                                being: name, serial number,
                                                hostMac, model and make. All
                                                columns are required. Below is
                                                an example of what a file should
                                                look like.
                                            </div>
                                            <div class="pt-2">
                                                <v-img
                                                    src="img/misc/deviceBulkUpload.png"
                                                ></v-img>
                                            </div>
                                        </div>
                                    </v-tooltip>
                                    <v-icon
                                        color="accent"
                                        @click="showHelp = !showHelp"
                                        >{{ helpIcon }}</v-icon
                                    >
                                </template>
                            </v-file-input>
                        </v-col>
                    </v-row>
                    <div v-show="items.length" class="text-body-1 text-right">
                        <span>Total records: {{ items.length }}</span>
                        <span v-show="invalidRecordsCount > 0">
                            - invalid records: {{ invalidRecordsCount }}</span
                        >
                    </div>
                </v-container>
            </v-card-title>
            <v-card-text class="scrollable-content pb-2 px-5">
                <div v-show="isDataLoading">
                    <v-progress-linear
                        v-model="progressValue"
                        :indeterminate="progressIndeterminate"
                    >
                    </v-progress-linear>
                </div>
                <v-simple-table
                    ref="uploadTable"
                    fixed-header
                    class="elevation-5"
                >
                    <template v-slot:default>
                        <thead>
                            <tr tabindex="0">
                                <th>Name *</th>
                                <th>Serial Number *</th>
                                <th>Host Mac *</th>
                                <th>Model *</th>
                                <th>Make *</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in items"
                                :id="`row-${index}`"
                                :key="`row-${index}`"
                                :class="{
                                    'success--text':
                                        item.processed && item.valid,
                                    'warning--text':
                                        item.processed && !item.valid
                                }"
                            >
                                <td>{{ item.name }}</td>
                                <td>{{ item.serialNumber }}</td>
                                <td>{{ item.hostMac }}</td>
                                <td>{{ item.model }}</td>
                                <td>{{ item.make }}</td>
                                <td>
                                    <v-icon
                                        v-show="item.valid"
                                        :color="item.processed ? 'success' : ''"
                                        >{{ icons.check }}</v-icon
                                    >
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <v-icon
                                                v-show="!item.valid"
                                                color="warning"
                                                v-on="on"
                                                >{{ icons.warning }}</v-icon
                                            >
                                        </template>
                                        <span>{{ item.message }}</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                            <tr v-if="!items.length">
                                <td
                                    colspan="8"
                                    class="text-center warning--text"
                                >
                                    No data selected
                                </td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
                <v-container pa-0 fluid>
                    <v-row no-gutters>
                        <v-col>
                            <v-btn
                                outlined
                                small
                                color="accent"
                                :disabled="isDataLoading"
                                @click="onDownloadClick"
                            >
                                Download template
                            </v-btn>
                        </v-col>
                        <v-col class="text-right">
                            <v-btn
                                outlined
                                small
                                :disabled="isDataLoading"
                                @click="onCloseClick"
                                >{{ closeButtonText }}</v-btn
                            >
                            <v-btn
                                color="primary"
                                small
                                class="ml-2"
                                :disabled="!allowAddButton"
                                :loading="isAddButtonLoading"
                                @click="onAddDevicesClick"
                                >Add Devices</v-btn
                            >
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';

export default {
    name: 'DeviceUploadDialog',
    props: {
        show: {
            type: Boolean,
            required: true
        },
        title: {
            type: String,
            required: false,
            default: 'Device bulk upload'
        },
        width: {
            type: String,
            required: false,
            default: '850'
        }
    },
    data() {
        return {
            isDataLoading: false,
            selectedFile: null,
            templateFileName: 'download/VMS_Devices_Template.xlsx',
            items: [],
            isAddButtonLoading: false,
            progressValue: 0,
            progressIndeterminate: true,
            refreshParentData: false,
            showHelp: false
        };
    },
    computed: {
        displayWidth() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                return this.width;
            }
            return '90%';
        },
        displayTitle() {
            return this.title;
        },
        helpIcon() {
            if (this.showHelp) {
                return this.icons.close;
            }
            return this.icons.help;
        },
        invalidRecordsCount() {
            return this.items.filter(item => !item.valid).length;
        },
        validRecordsCount() {
            return this.items.filter(item => item.valid).length;
        },
        processedRecordCount() {
            return this.items.filter(item => item.processed && item.valid)
                .length;
        },
        unprocessedRecordCount() {
            return this.validRecordsCount - this.processedRecordCount;
        },
        allowAddButton() {
            return (
                this.validRecordsCount > 0 &&
                this.unprocessedRecordCount > 0 &&
                !this.isAddButtonLoading &&
                !this.isDataLoading
            );
        },
        closeButtonText() {
            if (this.progressValue > 0) {
                return 'Close';
            }
            return 'Cancel';
        }
    },
    watch: {
        show(value) {
            if (!value) {
                this.resetDialog();
            }
        },
        selectedFile(value) {
            this.readExcelFile(value);
        }
    },
    methods: {
        ...mapActions('device', ['createDevice', 'checkIsDeviceUnique']),
        // =========================== Events ===============================
        onCloseClick() {
            this.$emit('close', this.refreshParentData);
        },
        onAddDevicesClick() {
            this.processItems(this.items);
        },
        onDownloadClick() {
            this.downloadFile(this.templateFileName);
        },
        onEscapeKeydown() {
            this.onCloseClick();
        },
        // ===================== Others ========================
        resetDialog() {
            this.isDataLoading = false;
            this.selectedFile = null;
            this.items = [];
            this.isAddButtonLoading = false;
            this.progressValue = 0;
            this.progressIndeterminate = true;
            this.refreshParentData = false;
        },
        readExcelFile(file) {
            if (file && file.name) {
                const reader = new FileReader();
                reader.onload = () => {
                    this.parseExcelFile(reader.result);
                };
                reader.readAsBinaryString(file);
            } else {
                this.items = [];
            }
        },
        async parseExcelFile(result) {
            const xlsx = await import('xlsx');
            try {
                this.isDataLoading = true;
                const doc = xlsx.read(result, {
                    type: 'binary'
                });
                // need to process each sheet separately
                this.items = [];
                for (const sheet of Object.keys(doc.Sheets)) {
                    this.parseExcelSheet(doc.Sheets[sheet]);
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        async parseExcelSheet(sheet) {
            const xlsx = await import('xlsx');
            const json = xlsx.utils.sheet_to_json(sheet).map(item => {
                const name = item.Name || item.name;
                const serialNumber =
                    item['Serial Number'] || item['serial number'];
                const hostMac = item.Hostmac || item.hostmac;
                const model = item.Model || item.model;
                const make = item.Make || item.make;
                let valid = true;
                let message = '';
                if (!(name && serialNumber && hostMac && model && make)) {
                    valid = false;
                    message = 'Required fields are missing';
                } else if (!this.isValidSerialNumber(serialNumber)) {
                    valid = false;
                    message =
                        'Invalid Serial Number. Should be 1-15 alpha numeric characters ';
                } else if (!this.isValidMacAddress(hostMac)) {
                    valid = false;
                    message =
                        'Invalid Host Mac. Should be six pairs of two alpha characters 0-9 or A-F';
                }
                return {
                    name,
                    serialNumber,
                    hostMac,
                    model,
                    make,
                    valid,
                    message,
                    processed: false
                };
            });
            this.items.push(...json);
        },
        downloadFile(fileUrl) {
            // will download the selected file
            if (fileUrl) {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', fileUrl);
                link.click();
            }
        },
        async processItems(items) {
            let progress = 0;
            try {
                this.isDataLoading = true;
                if (items && items.length) {
                    this.progressIndeterminate = false;
                    for (const item of items) {
                        progress += 1;
                        this.progressValue = Math.round(
                            (progress / items.length) * 100
                        );
                        this.scrollToRow(progress);
                        if (item.valid) {
                            item.processed = true;
                            const result = await this.preSaveCheck(item);
                            if (!result.valid) {
                                item.valid = result.valid;
                                item.message = result.message;
                            } else {
                                this.saveItem(item);
                            }
                        }
                    }
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        async preSaveCheck(item) {
            // will check if the device exists already.
            let result = {
                valid: true,
                message: ''
            };
            const params = {
                name: String(item.name),
                serialNumber: String(item.serialNumber),
                hostMac: String(item.hostMac),
                model: String(item.model),
                make: String(item.make),
                isNewRecord: true
            };
            const res = await this.checkIsDeviceUnique(params);
            if (!res) {
                result = {
                    valid: false,
                    message:
                        'Duplicate device! A device with same Host Mac already exist'
                };
            }
            return result;
        },
        async saveItem(item) {
            if (item) {
                try {
                    const device = {
                        name: String(item.name),
                        serialNumber: String(item.serialNumber),
                        hostMac: String(item.hostMac),
                        model: String(item.model),
                        make: String(item.make)
                    };
                    const res = await this.createDevice({ device });
                    item.processed = true;
                    item.message = 'Device created successfully. ID:' + res.id;
                    this.refreshParentData = true;
                } catch (error) {
                    let message = error.toString();
                    if (error.response && error.response.data) {
                        message = JSON.stringify(error.response.data);
                    }
                    item.processed = true;
                    item.valid = false;
                    item.message = message;
                } finally {
                    // ==========
                }
            }
        },
        scrollToRow(rowIndex) {
            const container = this.$refs.uploadTable.$el.querySelector(
                '.v-data-table__wrapper'
            );
            const row = this.$refs.uploadTable.$el.querySelector(
                `.v-data-table__wrapper tbody tr:nth-child(${rowIndex})`
            );
            if (container && row) {
                container.scrollTop = row.offsetTop - 48;
            }
        },
        isValidMacAddress(value) {
            const reg = new RegExp('^[a-fA-F0-9:]{17}$', 'g');
            return reg.test(value);
        },
        isValidSerialNumber(value) {
            const reg = new RegExp('^[a-z0-9A-Z]{1,15}$', 'g');
            return reg.test(value);
        }
    }
};
</script>

<style lang="scss" scoped></style>
